import { CommonModule } from '@angular/common'
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  DOCUMENT
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms'
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router'
import { Subject, debounceTime, filter, firstValueFrom, from, switchMap, takeUntil } from 'rxjs'

import { environment } from '../../../../environments/environment'
import { CaseInputComponent, HasPermissionDirective } from 'src/app/abacus'
import { HtmlTooltipDirective } from '../../../abacus/directives/html-tooltip.directive'
import { AuthService } from '../../../abacus/services/auth.service'
import { User } from '../../../abacus/interfaces/resources/user.interface'
import { CaseCreateEditComponent } from '../../../abacus/components/case-create-edit.component'
import { Field } from '../../../abacus/interfaces/field.interface'
import { ResourceDefinition } from '../../../abacus/interfaces/resource-definition.interface'
import { BreadcrumbService } from '../../../abacus/services/breadcrumb.service'
import { FlashMessageService } from '../../../abacus/services/flash-message.service'
import { ResourceService } from '../../../abacus/services/resource.service'
import { transparencyReportDefinition } from '../transparency-report.definition'
import {
  CheckboxItem,
  MatrixAnswer,
  MatrixConfig,
  QuestionnaireField,
  QuestionnaireQuestion,
  QuestionnaireSection,
  ReportingPeriod,
  ReportingPeriodWithStatus,
  Submission,
  SubmitStep
} from '../models/questionnaire.model'
import { mapQuestionsToFields, populateFieldValues } from './questionnaire-field-mapper'
import { evaluateShowWhen } from './show-when.util'
import { buildAnswersPayload, flattenQuestions } from './answers-payload.util'
import { commitFormValuesToFields } from './commit-form-values.util'
import {
  calculateAllSectionProgress,
  calculateCompletionPercentage,
  calculateSectionProgressFromForm
} from './section-progress.util'
import { QuestionnaireReviewComponent } from './questionnaire-review/questionnaire-review.component'
import { ReportingPeriodsListComponent } from './reporting-periods-list/reporting-periods-list.component'
import { CarryForwardPreviewComponent } from './carry-forward-preview/carry-forward-preview.component'
import { MatrixInputComponent } from './matrix-input/matrix-input.component'
import { RefSearchInputComponent } from './ref-search-input/ref-search-input.component'
import { InputType } from '../../../abacus/enums/input-type.enum'
import { toolDefinition } from '../../tool/tool.definition'
import { metricDefinition } from '../../metric/metric.definition'

type SaveCurrentSectionResult = 'saved' | 'skipped' | 'failed'

const VALID_STEPS: SubmitStep[] = ['reporting-periods', 'carry-forward', 'disclaimer', 'edit', 'review']

const ACTOR_DESCRIPTIONS: Record<string, string> = {
  'Model developers': 'Entities that design, build, train AI models, including through the selection of training data and model architecture. Among models, \u201cfoundation\u201d models and General Purpose AI Model (GPAI) are machine learning models that can perform a wide range of downstream tasks and may therefore be integrated into a wide range of downstream applications.',
  'Application developers': 'Organisations that develop AI applications or services that integrate AI models and make the resulting application or service available to AI application deployers. AI applications may be provided as a product or service (for example, using software as a service).',
  'Application deployers': 'Organisations that use an AI application. Deployers typically have day-to-day operational control over the AI application and are deploying it to satisfy a specific use case.'
}

@Component({
  templateUrl: './transparency-report-submit.component.html',
  styleUrls: ['./transparency-report-submit.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    HasPermissionDirective,
    CaseInputComponent,
    QuestionnaireReviewComponent,
    ReportingPeriodsListComponent,
    CarryForwardPreviewComponent,
    MatrixInputComponent,
    RefSearchInputComponent,
    HtmlTooltipDirective
  ]
})
export class TransparencyReportSubmitComponent
  extends CaseCreateEditComponent
  implements OnInit, OnDestroy
{
  destroyRef = inject(DestroyRef)
  cdr = inject(ChangeDetectorRef)
  private document = inject(DOCUMENT)
  private componentRouter = inject(Router)
  private componentActivatedRoute = inject(ActivatedRoute)
  private componentBreadcrumbService = inject(BreadcrumbService)
  private componentFlashMessageService = inject(FlashMessageService)
  public componentResourceService = inject(ResourceService)
  private authService = inject(AuthService)

  isNotProduction = !environment.production
  isAdmin = false

  definition: ResourceDefinition = {
    ...transparencyReportDefinition,
    title: 'Submit a Report'
  }

  fields: Field[] = []
  questionnaire: QuestionnaireSection[] = []
  private questionnaireLoadedForPeriod: number | null = null
  submission: Submission | null = null
  currentStep: SubmitStep = 'reporting-periods'
  reportingPeriodValue: number | null = null
  currentSectionNumber: number | null = null
  confirmSubmit = false
  loading = false
  currentSection: QuestionnaireSection | null = null

  periods: ReportingPeriodWithStatus[] = []
  previousSubmission: Submission | null = null
  showDiscardConfirm = false
  profileIncomplete = false
  private allSubmissions: Submission[] = []

  expandedSection: number | null = null
  sectionFields: Map<number, Field[]> = new Map()
  sectionResolvedFields: Map<number, Field[]> = new Map()
  sectionProgress: Map<number, number> = new Map()

  disclaimerChecked: boolean = false

  InputType = InputType
  toolResources = [toolDefinition]
  metricResources = [metricDefinition]
  refs: Record<number, { toolIds: number[]; metricIds: number[] }> = {}
  private questionIdByKey: Record<string, number> = {}
  private questionKeyById: Record<number, string> = {}

  private autoSaveSubject = new Subject<void>()
  private sectionTeardown$ = new Subject<void>()
  private skipNextQueryParamsUpdate = false
  private initializing = false

  constructor() {
    super(
      inject(UntypedFormBuilder),
      inject(Router),
      inject(BreadcrumbService),
      inject(ResourceService),
      inject(FlashMessageService),
      inject(ActivatedRoute)
    )
  }

  async ngOnInit() {
    this.document.body.style.overflow = 'visible'
    this.isAdmin = await this.authService.can('browseTransparencyReports')

    const user = await firstValueFrom(this.authService.me()) as User | false
    if (user) {
      const profile = user.transparencyReportProfile
      this.profileIncomplete = !profile?.organizationName || !profile?.country || !profile?.website
    }

    this.updateBreadcrumbs()
    await this.loadPeriodsAndSubmissions()

    this.componentActivatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((params: Params) => {
          if (this.skipNextQueryParamsUpdate) {
            this.skipNextQueryParamsUpdate = false
            return false
          }
          return true
        }),
        switchMap((params: Params) => from(this.handleQueryParams(params)))
      )
      .subscribe()

    this.autoSaveSubject
      .pipe(debounceTime(3000), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.autoSave())
  }

  private async handleQueryParams(params: Params): Promise<void> {
    const step = (params['step'] as string) || 'reporting-periods'
    const reportingPeriod = params['reportingPeriod'] ? parseInt(params['reportingPeriod'], 10) : null
    const section = params['section'] ? parseInt(params['section'], 10) : null

    if (!VALID_STEPS.includes(step as SubmitStep)) {
      this.navigateTo('reporting-periods')
      return
    }

    if (step === 'disclaimer') {
      this.disclaimerChecked = false
    }

    this.currentStep = step as SubmitStep
    this.reportingPeriodValue = reportingPeriod
    this.currentSectionNumber = section
    this.updateBreadcrumbs()

    if (this.currentStep === 'reporting-periods' && params['t']) {
      await this.loadPeriodsAndSubmissions()
      this.submission = null
      this.showDiscardConfirm = false
      return
    }

    if (this.currentStep !== 'reporting-periods' && this.reportingPeriodValue) {
      const periodExists = this.periods.some(p => p.value === this.reportingPeriodValue)
      if (!periodExists) {
        this.navigateTo('reporting-periods')
        return
      }

      if (!this.submission || this.submission.reportingPeriod !== this.reportingPeriodValue) {
        const existing = this.allSubmissions.find(s => s.reportingPeriod === this.reportingPeriodValue)
        if (existing) {
          await this.loadSubmission(existing.id)
        } else if (!this.initializing) {
          this.navigateTo('reporting-periods')
          return
        }
      }
    }

    await this.validateAndRedirect()

    if (this.currentStep === 'carry-forward' && this.submission?.copiedFromSubmissionId && !this.previousSubmission) {
      const fullPrevious = await this.componentResourceService.show(
        'transparency-reports',
        this.submission.copiedFromSubmissionId
      )
      this.previousSubmission = fullPrevious as Submission
    }

    if ((this.currentStep === 'carry-forward' || this.currentStep === 'edit' || this.currentStep === 'review') && this.reportingPeriodValue) {
      if (!this.questionnaire.length || this.questionnaireLoadedForPeriod !== this.reportingPeriodValue) {
        await this.loadQuestionnaire(this.reportingPeriodValue)
      }
    }

    if (this.currentStep === 'edit' && this.submission) {
      await this.setupAllSections()
      this.updateAllSectionProgress()

      if (this.currentSectionNumber != null) {
        this.expandedSection = this.currentSectionNumber
        this.currentSection = this.questionnaire.find(s => s.sectionNumber === this.currentSectionNumber) || null
        this.fields = this.sectionFields.get(this.currentSectionNumber) || []
        this.resolvedFields = this.sectionResolvedFields.get(this.currentSectionNumber) || []

        if (this.currentSection) {
          this.sectionTeardown$.next()
          this.form = await this.generateForm(this.resolvedFields)
          this.setupConditionalVisibility()
          this.form.valueChanges
            .pipe(takeUntil(this.sectionTeardown$))
            .subscribe(() => {
              this.autoSaveSubject.next()
              this.updateCurrentSectionProgress()
            })
        }
      } else {
        this.expandedSection = null
        this.currentSection = null
        this.fields = []
        this.resolvedFields = []
      }
      this.cdr.detectChanges()
    }
  }

  private async validateAndRedirect(): Promise<void> {
    const isReadOnlySubmission = (
      status: string
    ): boolean => ['published', 'unchanged', 'pending_review'].includes(status)

    if (this.currentStep === 'reporting-periods') {
      await this.loadPeriodsAndSubmissions()
      return
    }

    if (!this.reportingPeriodValue || !this.submission) {
      this.navigateTo('reporting-periods')
      return
    }

    if (isReadOnlySubmission(this.submission.status)) {
      if (this.currentStep !== 'review') {
        this.navigateTo('review', { reportingPeriod: this.reportingPeriodValue })
      }
      return
    }

    if (!this.submission.haveReadDisclaimer && this.currentStep !== 'disclaimer') {
      this.navigateTo('disclaimer', { reportingPeriod: this.reportingPeriodValue })
      return
    }

    if (this.currentStep === 'review' && this.submission.completionPercentage !== 100) {
      this.navigateTo('edit', { reportingPeriod: this.reportingPeriodValue })
    }
  }

  private async loadQuestionnaire(period: number): Promise<void> {
    try {
      const result = await this.componentResourceService.list(
        'transparency-reports/questionnaire',
        { period }
      )
      this.questionnaire = result as QuestionnaireSection[]
      this.questionnaireLoadedForPeriod = period
    } catch {
      this.componentFlashMessageService.error('Failed to load questionnaire')
    }
  }

  private async loadSubmission(id: string | number): Promise<void> {
    try {
      const result = await this.componentResourceService.show(
        'transparency-reports',
        id
      )
      this.submission = result as Submission
      this.showDiscardConfirm = false
    } catch {
      this.componentFlashMessageService.error('Failed to load submission')
    }
  }

  private async loadPeriodsAndSubmissions(): Promise<void> {
    try {
      const [periodsResult, submissionsResult] = await Promise.all([
        this.componentResourceService.list('transparency-reports/reporting-periods'),
        this.componentResourceService.list('transparency-reports/mine')
      ])

      const rawPeriods = periodsResult as ReportingPeriod[]
      this.allSubmissions = submissionsResult as Submission[]

      this.periods = this.buildPeriodsList(rawPeriods, this.allSubmissions)
    } catch {
      this.componentFlashMessageService.error('Failed to load reporting periods')
    }
  }

  buildPeriodsList(periods: ReportingPeriod[], submissions: Submission[]): ReportingPeriodWithStatus[] {
    return periods
      .map(period => {
        const submission = submissions.find(s => s.reportingPeriod === period.value) || null
        let status: ReportingPeriodWithStatus['status'] = 'not_started'
        if (submission) {
          status = submission.status as ReportingPeriodWithStatus['status']
        }
        return { ...period, submission, status }
      })
      .sort((a, b) => b.value - a.value)
  }

  async onPeriodSelected(period: ReportingPeriodWithStatus): Promise<void> {
    this.loading = true
    try {
      if (period.submission) {
        await this.loadSubmission(period.submission.id)

        if (period.status === 'draft') {
          if (!period.submission.haveReadDisclaimer) {
            this.navigateTo('disclaimer', { reportingPeriod: period.value })
          } else if (this.submission?.copiedFromSubmissionId) {
            const fullPrevious = await this.componentResourceService.show(
              'transparency-reports',
              this.submission.copiedFromSubmissionId
            )
            this.previousSubmission = fullPrevious as Submission
            this.navigateTo('carry-forward', { reportingPeriod: period.value })
          } else {
            this.navigateTo('edit', { reportingPeriod: period.value })
          }
        } else {
          this.navigateTo('review', { reportingPeriod: period.value })
        }
      } else {
        if (this.profileIncomplete) return

        const previousSubmission = this.findPreviousPublishedSubmission(period.value)

        this.initializing = true
        try {
          const body: any = { reportingPeriod: period.value }
          if (previousSubmission) {
            body.copyFromSubmissionId = previousSubmission.id
          }
          const result = await this.componentResourceService.store(
            'transparency-reports',
            body
          )
          this.submission = result as Submission
          this.showDiscardConfirm = false
          this.allSubmissions.push(this.submission)
          this.periods = this.buildPeriodsList(
            this.periods.map(p => ({ value: p.value, label: p.label, year: p.year, quarter: p.quarter })),
            this.allSubmissions
          )

          this.navigateTo('disclaimer', { reportingPeriod: period.value })
        } finally {
          this.initializing = false
        }
      }
    } catch {
      this.componentFlashMessageService.error('Failed to start submission')
    } finally {
      this.loading = false
    }
  }

  private findPreviousPublishedSubmission(currentPeriodValue: number): Submission | null {
    const currentPeriod = this.periods.find(p => p.value === currentPeriodValue)
    if (!currentPeriod) return null

    const previousPeriods = this.periods
      .filter(p => p.value < currentPeriodValue && p.versionKey === currentPeriod.versionKey)
      .sort((a, b) => b.value - a.value)

    const immediatePrevious = previousPeriods[0]
    if (!immediatePrevious) return null

    if (immediatePrevious.submission && ['published', 'unchanged'].includes(immediatePrevious.submission.status)) {
      return immediatePrevious.submission
    }
    return null
  }

  async onCarryForwardConfirm(): Promise<void> {
    if (this.loading) return
    if (!this.submission) return

    if (!this.submission.haveReadDisclaimer) {
      this.navigateTo('disclaimer', { reportingPeriod: this.reportingPeriodValue })
      return
    }

    this.loading = true
    try {
      await this.componentResourceService.patch(
        `transparency-reports/${this.submission.id}/submit`
      )
      this.componentFlashMessageService.success('Your report has been carried forward as unchanged')

      await this.loadPeriodsAndSubmissions()
      this.submission = null
      this.navigateTo('reporting-periods')
    } catch {
      this.componentFlashMessageService.error('Failed to carry forward report')
    } finally {
      this.loading = false
    }
  }

  async onCarryForwardStartFresh(): Promise<void> {
    if (!this.submission || !this.reportingPeriodValue) return

    this.loading = true
    try {
      await this.componentResourceService.patch(
        `transparency-reports/${this.submission.id}/clear-answers`
      )
      this.submission.copiedFromSubmissionId = null
      this.submission.completionPercentage = 0
      this.submission.answers = []
      this.previousSubmission = null
      this.navigateTo('disclaimer', { reportingPeriod: this.reportingPeriodValue })
    } catch {
      this.componentFlashMessageService.error('Failed to clear answers')
    } finally {
      this.loading = false
    }
  }

  onCarryForwardEdit(): void {
    if (!this.submission || !this.reportingPeriodValue) return

    if (this.submission.haveReadDisclaimer) {
      this.navigateTo('edit', { reportingPeriod: this.reportingPeriodValue })
    } else {
      this.navigateTo('disclaimer', { reportingPeriod: this.reportingPeriodValue })
    }
  }

  async acceptDisclaimer(): Promise<void> {
    if (!this.submission || !this.disclaimerChecked) return

    this.loading = true
    try {
      await this.componentResourceService.patch(
        `transparency-reports/${this.submission.id}/disclaimer`
      )
      this.submission.haveReadDisclaimer = true

      if (this.submission.copiedFromSubmissionId) {
        this.navigateTo('carry-forward', { reportingPeriod: this.reportingPeriodValue })
      } else {
        this.navigateTo('edit', { reportingPeriod: this.reportingPeriodValue })
      }
    } catch {
      this.componentFlashMessageService.error('Failed to accept disclaimer')
    } finally {
      this.loading = false
    }
  }

  private async autoSave(): Promise<void> {
    const section = this.expandedSection ?? this.currentSectionNumber
    if (!this.submission || !this.form || section == null || section < 0 || section > this.questionnaire.length) return

    try {
      const answers = this.buildAnswersPayload()
      await this.componentResourceService.patch(
        `transparency-reports/${this.submission.id}/answers`,
        { answers, currentSection: section }
      )
      this.componentFlashMessageService.success('Progress saved')
    } catch {
      this.componentFlashMessageService.error('Auto-save failed. Please try saving again.')
    }
  }

  private buildAnswersPayload() {
    return buildAnswersPayload(this.currentSection, this.form, this.resolvedFields, this.refs)
  }

  navigateTo(step: SubmitStep, params: Record<string, any> = {}): void {
    if (step === 'reporting-periods') {
      this.componentRouter.navigate([], {
        queryParams: {},
        queryParamsHandling: ''
      })
      return
    }

    this.componentRouter.navigate([], {
      queryParams: { step, ...params },
      queryParamsHandling: ''
    })
  }

  async saveAndNavigateTo(step: SubmitStep, params: Record<string, any> = {}): Promise<void> {
    if (this.currentStep === 'edit' && this.submission && this.expandedSection != null) {
      this.loading = true
      try {
        const answers = this.buildAnswersPayload()
        await this.componentResourceService.patch(
          `transparency-reports/${this.submission.id}/answers`,
          { answers, currentSection: this.expandedSection }
        )

        const result = await this.componentResourceService.show(
          'transparency-reports',
          this.submission.id
        )
        this.submission = result as Submission
      } catch {
        this.componentFlashMessageService.error('Failed to save answers')
        this.loading = false
        return
      }
      this.loading = false
    }

    this.navigateTo(step, params)
  }

  async unsubmitReport(): Promise<void> {
    if (!this.submission) return

    this.loading = true
    try {
      await this.componentResourceService.patch(
        `transparency-reports/${this.submission.id}/unsubmit`
      )
      this.submission.status = 'draft'
      this.navigateTo('edit', { reportingPeriod: this.reportingPeriodValue })
    } catch {
      this.componentFlashMessageService.error('Failed to unsubmit report')
    } finally {
      this.loading = false
    }
  }

  async submitReport(): Promise<void> {
    if (!this.submission) return
    if (!this.confirmSubmit) {
      this.componentFlashMessageService.error('Please confirm that the information provided is accurate before submitting.')
      return
    }

    this.loading = true
    try {
      await this.componentResourceService.patch(
        `transparency-reports/${this.submission.id}/submit`
      )
      this.componentFlashMessageService.success('Your report has been submitted')

      await this.loadPeriodsAndSubmissions()
      this.submission = null
      this.confirmSubmit = false
      this.navigateTo('reporting-periods')
    } catch {
      this.componentFlashMessageService.error('Failed to submit report')
    } finally {
      this.loading = false
    }
  }

  private updateBreadcrumbs(): void {
    const stepLabels: Record<SubmitStep, string> = {
      'reporting-periods': 'Reporting periods',
      'carry-forward': 'Carry forward',
      'disclaimer': 'Disclaimer',
      'edit': 'Edit',
      'review': 'Review'
    }

    const selectedPeriod = this.reportingPeriodValue
      ? this.periods.find(p => p.value === this.reportingPeriodValue)
      : null

    const submitPath = '/transparency-reports/submit'
    const links: Array<{ path?: string; label: string }> = []

    if (this.currentStep === 'reporting-periods') {
      links.push({ label: 'Reporting periods' })
    } else {
      links.push({ path: submitPath, label: 'Reporting periods' })
      const stepLabel = selectedPeriod
        ? `${selectedPeriod.label} - ${stepLabels[this.currentStep]}`
        : stepLabels[this.currentStep]
      links.push({ label: stepLabel })
    }

    this.componentBreadcrumbService.breadcrumbLinks.next(links)
  }

  discardDraft(): void {
    this.showDiscardConfirm = true
  }

  cancelDiscard(): void {
    this.showDiscardConfirm = false
  }

  async confirmDiscard(): Promise<void> {
    if (!this.submission) return

    this.loading = true
    try {
      await this.componentResourceService.delete(
        this.definition.slug,
        this.submission.id
      )
      this.componentFlashMessageService.success('Draft discarded successfully')
      this.showDiscardConfirm = false
      this.submission = null
      await this.loadPeriodsAndSubmissions()
      this.navigateTo('reporting-periods')
    } catch {
      this.componentFlashMessageService.error('Failed to discard draft')
      this.showDiscardConfirm = false
    } finally {
      this.loading = false
    }
  }

  onMatrixValueChanged(value: MatrixAnswer, field: QuestionnaireField): void {
    field.matrixValue = value.selections
    field.matrixSpecifyText = value.specifyText || {}
    const answer: MatrixAnswer = { selections: value.selections }
    const specifyText = this.buildMatrixSpecifyText(field, value)
    if (Object.keys(specifyText).length > 0) {
      answer.specifyText = specifyText
    }
    this.form.get(field.property)?.setValue(JSON.stringify(answer))
    this.autoSaveSubject.next()
  }

  private buildMatrixSpecifyText(
    field: QuestionnaireField,
    value: MatrixAnswer
  ): Record<string, string> {
    const config = field.matrixConfig
    const storedSpecify = value.specifyText || {}
    const result: Record<string, string> = {}
    for (const row of config?.rows || []) {
      if (row.hasSpecify && value.selections[row.key] && storedSpecify[row.key]) {
        result[row.key] = storedSpecify[row.key]
      }
    }
    return result
  }

  isCheckboxItemSelected(field: Field, value: string): boolean {
    const selected = this.form.get(field.property)?.value
    return Array.isArray(selected) && selected.includes(value)
  }

  isSpecifyTextMissing(field: QuestionnaireField, itemValue: string): boolean {
    return !field.specifyText?.[itemValue]?.trim()
  }

  onSpecifyTextChange(field: QuestionnaireField, itemValue: string, event: Event): void {
    const text = (event.target as HTMLInputElement).value
    if (!field.specifyText) field.specifyText = {}
    field.specifyText[itemValue] = text
    this.updateCurrentSectionProgress()
    this.autoSaveSubject.next()
  }

  getQuestionId(field: Field): number {
    return (field as QuestionnaireField).questionId || this.questionIdByKey[field.property] || 0
  }

  getToolRefInitialValue(questionId: number): number[] {
    if (!this.refs[questionId]) this.refs[questionId] = { toolIds: [], metricIds: [] }
    return this.refs[questionId].toolIds
  }

  getMetricRefInitialValue(questionId: number): number[] {
    if (!this.refs[questionId]) this.refs[questionId] = { toolIds: [], metricIds: [] }
    return this.refs[questionId].metricIds
  }

  hasTextValue(field: Field): boolean {
    const value = this.form?.get(field.property)?.value
    return typeof value === 'string' && value.trim().length > 0
  }

  getActorDescription(actor: string): string {
    return ACTOR_DESCRIPTIONS[actor] || ''
  }

  shouldShowRefs(field: QuestionnaireField): boolean {
    return !!field.collectRefs
  }

  hasCheckboxSelection(field: Field): boolean {
    const value = this.form?.get(field.property)?.value
    return Array.isArray(value) && value.length > 0
  }

  hasRadioYes(field: Field): boolean {
    return this.form?.get(field.property)?.value === 'Yes'
  }

  onRefChanged(questionId: number, type: 'tool' | 'metric', value: any): void {
    if (!this.refs[questionId]) this.refs[questionId] = { toolIds: [], metricIds: [] }
    if (type === 'tool') {
      this.refs[questionId].toolIds = value.toolIds || []
    } else {
      this.refs[questionId].metricIds = value.metricIds || []
    }
    this.autoSaveSubject.next()
  }

  override onValueChanged(newValue: any, field: Field): void {
    super.onValueChanged(newValue, field)
    const qId = this.getQuestionId(field)
    if (!qId) return

    if (field.inputType === InputType.RichText) {
      const text = typeof newValue === 'string' ? newValue.trim() : ''
      if (!text && this.refs[qId]) {
        this.refs[qId] = { toolIds: [], metricIds: [] }
      }
    } else if (field.inputType === InputType.Radio) {
      if (newValue !== 'Yes' && this.refs[qId]) {
        this.refs[qId] = { toolIds: [], metricIds: [] }
      }
    } else if ((field as QuestionnaireField).isCheckbox) {
      const selected = Array.isArray(newValue) ? newValue : []
      if (selected.length === 0 && this.refs[qId]) {
        this.refs[qId] = { toolIds: [], metricIds: [] }
      }
    }
  }

  async fillWithRandomData(): Promise<void> {
    if (!this.submission || !this.questionnaire.length) return

    this.loading = true
    try {
      const [toolResults, metricResults] = await Promise.all([
        this.componentResourceService.list('search', { resources: ['Tool'], terms: '' }),
        this.componentResourceService.list('search', { resources: ['Metric'], terms: '' })
      ])
      const availableTools = (toolResults as any[]) || []
      const availableMetrics = (metricResults as any[]) || []

      for (const section of this.questionnaire) {
        const answers: Array<{ questionId: number; answer: string; toolIds?: number[]; metricIds?: number[] }> = []

        const allQuestions = flattenQuestions(section.questions)
        for (const question of allQuestions) {
          const refData = this.pickRandomRefs(question, availableTools, availableMetrics)

          if (question.type === 'empty') {
            if (question.collectRefs && (refData.toolIds?.length || refData.metricIds?.length)) {
              answers.push({ questionId: question.id, answer: '', ...refData })
            }
            continue
          }

          if (question.type === 'text') {
            const title = question.title.replace(/<[^>]*>/g, '').substring(0, 50)
            answers.push({
              questionId: question.id,
              answer: `<p><strong>Sample response</strong> for <em>"${title}"</em>.</p><ul><li>First point of detail</li><li>Second point with <strong>emphasis</strong></li></ul>`,
              ...refData
            })
          } else if (question.type === 'radio') {
            const items = (question.items as { label: string; value?: string }[]) || []
            if (items.length) {
              const hasYes = items.some(i => (i.value ?? i.label) === 'Yes')
              const item = hasYes && question.collectRefs
                ? items.find(i => (i.value ?? i.label) === 'Yes')!
                : items[Math.floor(Math.random() * items.length)]
              answers.push({
                questionId: question.id,
                answer: item.value ?? item.label,
                ...((item.value ?? item.label) === 'Yes' ? refData : {})
              })
            }
          } else if (question.type === 'checkbox') {
            const items = (question.items as CheckboxItem[]) || []
            if (items.length) {
              const count = Math.max(1, Math.floor(Math.random() * Math.min(3, items.length)))
              const shuffled = [...items].sort(() => Math.random() - 0.5)
              const selectedItems = shuffled.slice(0, count)
              const selected = selectedItems.map(i => i.value)
              const specifyText: Record<string, string> = {}
              for (const item of selectedItems) {
                if (item.hasSpecify) {
                  specifyText[item.value] = `Sample specification for "${item.label}"`
                }
              }
              const checkboxAnswer: { selected: string[]; specifyText?: Record<string, string> } = { selected }
              if (Object.keys(specifyText).length > 0) {
                checkboxAnswer.specifyText = specifyText
              }
              answers.push({
                questionId: question.id,
                answer: JSON.stringify(checkboxAnswer),
                ...refData
              })
            }
          } else if (question.type === 'matrix') {
            const config = question.items as { rows: { key: string; label: string; hasSpecify?: boolean }[]; columns: { key: string }[] } | null
            if (config?.rows?.length && config?.columns?.length) {
              const selections: Record<string, string> = {}
              const specifyText: Record<string, string> = {}
              for (const row of config.rows) {
                selections[row.key] = config.columns[Math.floor(Math.random() * config.columns.length)].key
                if (row.hasSpecify) {
                  specifyText[row.key] = `Sample specification for "${row.label}"`
                }
              }
              const matrixAnswer: { selections: Record<string, string>; specifyText?: Record<string, string> } = { selections }
              if (Object.keys(specifyText).length > 0) {
                matrixAnswer.specifyText = specifyText
              }
              answers.push({
                questionId: question.id,
                answer: JSON.stringify(matrixAnswer)
              })
            }
          }
        }

        if (answers.length) {
          await this.componentResourceService.patch(
            `transparency-reports/${this.submission.id}/answers`,
            { answers, currentSection: section.sectionNumber }
          )
        }
      }

      await this.loadSubmission(this.submission.id)
      await this.setupAllSections()
      this.updateAllSectionProgress()
      this.componentFlashMessageService.success('Filled all sections with random data')

      if (this.expandedSection != null && this.currentSection) {
        this.fields = this.sectionFields.get(this.expandedSection) || []
        this.resolvedFields = this.sectionResolvedFields.get(this.expandedSection) || []
        this.sectionTeardown$.next()
        this.form = await this.generateForm(this.resolvedFields)
        this.setupConditionalVisibility()
        this.form.valueChanges
          .pipe(takeUntil(this.sectionTeardown$))
          .subscribe(() => {
            this.autoSaveSubject.next()
            this.updateCurrentSectionProgress()
          })
      }

      this.cdr.detectChanges()
    } catch {
      this.componentFlashMessageService.error('Failed to fill with random data')
    } finally {
      this.loading = false
    }
  }

  private pickRandomRefs(
    question: QuestionnaireQuestion,
    tools: any[],
    metrics: any[]
  ): { toolIds?: number[]; metricIds?: number[] } {
    if (!question.collectRefs) return {}
    const result: { toolIds?: number[]; metricIds?: number[] } = {}
    if (tools.length) {
      const count = Math.min(1 + Math.floor(Math.random() * 2), tools.length)
      const shuffled = [...tools].sort(() => Math.random() - 0.5)
      result.toolIds = shuffled.slice(0, count).map(t => t.id)
    }
    if (metrics.length) {
      const count = Math.min(1 + Math.floor(Math.random() * 2), metrics.length)
      const shuffled = [...metrics].sort(() => Math.random() - 0.5)
      result.metricIds = shuffled.slice(0, count).map(m => m.id)
    }
    return result
  }

  ngOnDestroy(): void {
    this.sectionTeardown$.next()
    this.sectionTeardown$.complete()
    this.document.body.style.overflow = ''
  }

  private async setupAllSections(): Promise<void> {
    const answersByQuestionId: Record<number, string> = {}
    const answersByKeyBySection = new Map<number, Record<string, string>>()
    const sectionByQuestionId = new Map<number, number>()

    for (const section of this.questionnaire) {
      answersByKeyBySection.set(section.sectionNumber, {})
      for (const q of flattenQuestions(section.questions)) {
        sectionByQuestionId.set(q.id, section.sectionNumber)
        this.questionIdByKey[q.questionKey] = q.id
        this.questionKeyById[q.id] = q.questionKey
      }
    }

    if (this.submission?.answers?.length) {
      for (const answer of this.submission.answers) {
        const sectionNumber = sectionByQuestionId.get(answer.questionId)
        if (sectionNumber === undefined) continue
        const questionKey = this.questionKeyById[answer.questionId]
        answersByQuestionId[answer.questionId] = answer.answer
        answersByKeyBySection.get(sectionNumber)![questionKey] = answer.answer
      }
    }

    this.refs = {}
    if (this.submission?.toolReferences) {
      for (const [qId, tools] of Object.entries(this.submission.toolReferences)) {
        const id = Number(qId)
        if (!this.refs[id]) this.refs[id] = { toolIds: [], metricIds: [] }
        this.refs[id].toolIds = tools.map(t => t.id)
      }
    }
    if (this.submission?.metricReferences) {
      for (const [qId, metrics] of Object.entries(this.submission.metricReferences)) {
        const id = Number(qId)
        if (!this.refs[id]) this.refs[id] = { toolIds: [], metricIds: [] }
        this.refs[id].metricIds = metrics.map(m => m.id)
      }
    }

    for (const section of this.questionnaire) {
      const fields = mapQuestionsToFields(section.questions)
      populateFieldValues(fields, answersByQuestionId)
      this.sectionFields.set(section.sectionNumber, fields)

      const sectionAnswersByKey = answersByKeyBySection.get(section.sectionNumber) || {}
      const resolved = (await this.resolveFields(fields)) as QuestionnaireField[]
      for (const field of resolved) {
        if (field.showWhenRule) {
          field.hidden = !evaluateShowWhen(field.showWhenRule, sectionAnswersByKey)
        }
      }
      this.sectionResolvedFields.set(section.sectionNumber, resolved)
    }
  }

  private setupConditionalVisibility(): void {
    if (!this.resolvedFields || !this.form) return

    const ruledFields = (this.resolvedFields as QuestionnaireField[]).filter(
      (f) => f.showWhenRule
    )

    for (const field of ruledFields) {
      const rule = field.showWhenRule
      const parentControl = this.form.get(rule.questionKey)
      if (!parentControl) continue

      field.hidden = !evaluateShowWhen(rule, {
        [rule.questionKey]: parentControl.value
      })

      parentControl.valueChanges
        .pipe(takeUntil(this.sectionTeardown$))
        .subscribe((value) => {
          field.hidden = !evaluateShowWhen(rule, {
            [rule.questionKey]: value
          })
          this.updateCurrentSectionProgress()
          this.cdr.detectChanges()
        })
    }

    this.form.valueChanges
      .pipe(takeUntil(this.sectionTeardown$))
      .subscribe(() => {
        for (const field of ruledFields) {
          const rule = field.showWhenRule
          const ctrl = this.form.get(rule.questionKey)
          if (!ctrl) continue
          field.hidden = !evaluateShowWhen(rule, {
            [rule.questionKey]: ctrl.value
          })
        }
        this.cdr.detectChanges()
      })
  }

  async toggleSection(sectionNumber: number): Promise<void> {
    if (this.expandedSection === sectionNumber) {
      const saveCurrentSectionResult = await this.saveCurrentSection()
      if (saveCurrentSectionResult === 'failed') return
      this.expandedSection = null
      this.currentSectionNumber = null
      this.currentSection = null
      this.fields = []
      this.resolvedFields = []
      this.skipNextQueryParamsUpdate = true
      this.componentRouter.navigate([], {
        queryParams: { step: 'edit', reportingPeriod: this.reportingPeriodValue },
        queryParamsHandling: ''
      })
      return
    }

    const saveCurrentSectionResult = await this.saveCurrentSection()
    if (saveCurrentSectionResult === 'failed') return
    this.expandedSection = sectionNumber
    this.currentSectionNumber = sectionNumber
    this.skipNextQueryParamsUpdate = true
    this.componentRouter.navigate([], {
      queryParams: { step: 'edit', reportingPeriod: this.reportingPeriodValue, section: sectionNumber },
      queryParamsHandling: ''
    })
    this.currentSection = this.questionnaire.find(s => s.sectionNumber === sectionNumber) || null
    this.fields = this.sectionFields.get(sectionNumber) || []
    this.resolvedFields = this.sectionResolvedFields.get(sectionNumber) || []

    if (this.currentSection) {
      this.sectionTeardown$.next()
      this.form = await this.generateForm(this.resolvedFields)
      this.setupConditionalVisibility()
      this.form.valueChanges
        .pipe(takeUntil(this.sectionTeardown$))
        .subscribe(() => {
          this.autoSaveSubject.next()
          this.updateCurrentSectionProgress()
        })
    }

    this.cdr.detectChanges()

    setTimeout(() => {
      const el = this.document.querySelector(`.accordion-panel:nth-child(${sectionNumber + 1}) .accordion-header`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  get isLastSection(): boolean {
    return this.expandedSection === this.questionnaire.length
  }

  async continueToNextSection(): Promise<void> {
    if (this.expandedSection == null) return

    const next = this.expandedSection + 1
    if (next > this.questionnaire.length) {
      await this.saveAndNavigateTo('review', { reportingPeriod: this.reportingPeriodValue })
      return
    }

    await this.toggleSection(next)
  }

  private async saveCurrentSection(): Promise<SaveCurrentSectionResult> {
    if (this.expandedSection == null || !this.submission || !this.form) {
      return 'skipped'
    }

    try {
      const answers = this.buildAnswersPayload()
      if (answers.length) {
        await this.componentResourceService.patch(
          `transparency-reports/${this.submission.id}/answers`,
          { answers, currentSection: this.expandedSection }
        )
      }

      this.commitFormValuesToCache()
      return 'saved'
    } catch {
      this.componentFlashMessageService.error('Auto-save failed. Please try saving again.')
      return 'failed'
    }
  }

  private commitFormValuesToCache(): void {
    if (this.expandedSection == null) return
    const fields = (this.sectionResolvedFields.get(this.expandedSection) || []) as QuestionnaireField[]
    commitFormValuesToFields(fields, this.form)
  }

  get completionPercentage(): number {
    return calculateCompletionPercentage(
      this.questionnaire,
      this.sectionResolvedFields,
      this.sectionProgress,
      this.submission?.completionPercentage ?? 0
    )
  }

  get currentPeriodLabel(): string {
    const period = this.periods.find(p => p.value === this.reportingPeriodValue)
    return period?.label ?? ''
  }

  get isReadOnly(): boolean {
    if (!this.submission) return false
    return ['published', 'unchanged', 'pending_review'].includes(this.submission.status)
  }

  getSectionProgress(sectionNumber: number): number {
    return this.sectionProgress.get(sectionNumber) ?? 0
  }

  goToEditSection(sectionNumber: number): void {
    this.navigateTo('edit', { reportingPeriod: this.reportingPeriodValue, section: sectionNumber })
  }

  private updateAllSectionProgress(): void {
    this.sectionProgress = calculateAllSectionProgress(
      this.questionnaire,
      this.sectionResolvedFields,
      this.submission
    )
  }

  private updateCurrentSectionProgress(): void {
    if (!this.currentSection || !this.form) return

    this.sectionProgress.set(
      this.currentSection.sectionNumber,
      calculateSectionProgressFromForm(this.currentSection, this.resolvedFields, this.form)
    )
  }
}
